import axios from "axios";
import { NFT_Validator_Address } from "./ABI";
import { get_Validator_NFT_contract } from "./Contract";

/**
 * MAALChain Validator NFT helpers.
 *
 * Validator NFTs are not stored by the backend. It keeps one system collection
 * for them (`IsValidator: true`, rank 1) and reads tokens from the contract on
 * request: every minted one for the collection page (fetchAllValidatorNfts), a
 * wallet's own for its profile (fetchValidatorNfts).
 *
 * The metadata's "Tier" and "Holding Value" are static files, while the contract's
 * getValidatorData changes with updateValidatorHoldings and issueBonus, so the
 * chain is the source for both. Holdings are whole MAAL, not wei.
 */

/**
 * The wallet's validator NFTs from POST /validatornfts, read live from the chain:
 * `{ NFTContract, tokenID, symbol, name, imageURL, attributes, tier, maalCoins, lockedTill, ... }`
 * with no `_id`. [] when there is no wallet, none are held or the call fails.
 */
export const fetchValidatorNfts = async (URL, wallet) => {
  if (!wallet) return [];
  try {
    const res = await axios.post(`${URL}/validatornfts`, { wallet });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

/** Validator NFTs minted per page of POST /allvalidatornfts. */
export const VALIDATOR_PAGE_SIZE = 12;

/**
 * One page of every minted validator NFT from POST /allvalidatornfts, whoever
 * holds it (`Owner: [wallet]`), same shape as fetchValidatorNfts. Public: no
 * login needed. [] when the call fails.
 */
export const fetchAllValidatorNfts = async (URL, page = 1) => {
  try {
    const res = await axios.post(`${URL}/allvalidatornfts`, { page });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const isValidatorNft = (contractAddress) =>
  Boolean(NFT_Validator_Address && contractAddress) &&
  contractAddress.toLowerCase() === NFT_Validator_Address.toLowerCase();

/** Lowercase contract owner: the only wallet that can mint, or "" when it cannot be read. */
export const getValidatorOwner = async () => {
  try {
    if (!NFT_Validator_Address) return "";
    const contract = await get_Validator_NFT_contract();
    return (await contract.methods.owner().call()).toLowerCase();
  } catch (error) {
    console.log(error);
    return "";
  }
};

/** `{ tier, maalCoins, lockedTill }` (lockedTill in ms), or null when it cannot be read. */
export const getValidatorData = async (tokenId) => {
  try {
    const contract = await get_Validator_NFT_contract();
    const data = await contract.methods.getValidatorData(tokenId).call();
    // An unminted token answers with zeros instead of reverting.
    if (Number(data.tier) === 0) return null;
    return {
      tier: Number(data.tier),
      maalCoins: data.maalCoins,
      lockedTill: Number(data.lockedTill) * 1000,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

/** Transfers revert with MAAL721StillLocked until lockedTill. */
export const isValidatorLocked = (data) => Boolean(data) && data.lockedTill > Date.now();

/** The metadata attributes with Tier and Holding Value taken from the chain, plus the lock end. */
export const withValidatorData = (attributes, data) => {
  if (!data) return attributes;
  const fromChain = {
    Tier: String(data.tier),
    // Same thousands separators as the metadata ("15,094,950"); maalCoins is a uint256 string.
    "Holding Value": String(data.maalCoins).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
  };
  const merged = (attributes || []).map((item) =>
    fromChain[item.trait_type] === undefined ? item : { ...item, value: fromChain[item.trait_type] }
  );
  for (const [trait_type, value] of Object.entries(fromChain)) {
    if (!merged.some((item) => item.trait_type === trait_type)) merged.push({ trait_type, value });
  }
  if (isValidatorLocked(data)) {
    merged.push({ trait_type: "Locked until", value: new Date(data.lockedTill).toLocaleDateString() });
  }
  return merged;
};
